import { TinyButton, tokens, User, UserLinkList } from 'ComponentLibrary'
import { Box, ButtonBase, styled } from '@mui/material'
import { PencilSimpleIcon, PlusIcon } from '@phosphor-icons/react'
import { type AppraiserAssignment as AppraiserAssignmentType } from 'ComponentLibrary/src/common/users'
import {
    AppraiserAssignmentModal,
    type AppraiserAssignmentModalCopy,
} from 'ComponentLibrary/src/widgets/AppraiserAssignment/AppraiserAssignmentModal'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'
import { resolveAppraiserAssignment } from './resolveAppraiserAssignment'

type BaseProps = {
    appraiserAssignment?: AppraiserAssignmentType
    users?: User[]
    size?: 'small' | 'medium'
    displayMode?: 'compact' | 'expanded'
    userLinkMaxWidth?: string
    externalAppraisingEntityName?: string
    copy?: Partial<AppraiserAssignmentCopy>
    userLinkCopy?: Partial<UserLinkCopy>
}

type EditableProps = BaseProps & {
    editAppraiserAssignment: (data: AppraiserAssignmentType | undefined) => void
    isPersistingAppraiserAssignment?: boolean
    open: boolean
    setOpen: (open: boolean) => void
    modalPrimaryButtonLabel?: string
    disabled?: boolean
}

type ReadonlyProps = BaseProps & {
    editAppraiserAssignment?: never
    isPersistingAppraiserAssignment?: never
    open?: never
    setOpen?: never
    modalPrimaryButtonLabel?: never
    disabled?: never
}

type AppraiserAssignmentProps = EditableProps | ReadonlyProps

export type AppraiserAssignmentCopy = {
    addButton: string
    modal: AppraiserAssignmentModalCopy
}

const DEFAULT_APPRAISER_ASSIGNMENT_COPY: AppraiserAssignmentCopy = {
    addButton: 'Gutachter/in hinzufügen',
    modal: {
        customOptionName: '► Extern (Freitext)',
        customOptionGroup: 'Nicht registrierte Gutachter',
        title: 'Zuständige Gutachter/innen',
        cancel: 'Abbrechen',
        duplicateWarning: 'Derselbe Gutachter wurde mehrfach ausgewählt. Bitte ändern Sie einen der Einträge.',
        customInfo:
            'Freitext bitte nur für nicht registrierte Gutachter verwenden. Automatisierungen funktionieren nur mit verknüpften Gutachtern.',
        mainAppraiser: 'Erstgutachter/in',
        secondaryAppraiser: 'Zweitgutachter/in',
        tertiaryAppraiser: 'Drittgutachter/in',
        nameAndCertification: 'Name und Zertifizierung',
        remove: 'Entfernen',
        addSecondary: 'Zweitgutachter/in hinzufügen',
        addTertiary: 'Drittgutachter/in hinzufügen',
    },
}

export function AppraiserAssignment({
    appraiserAssignment,
    disabled,
    users,
    size,
    displayMode = 'compact',
    userLinkMaxWidth,
    editAppraiserAssignment,
    isPersistingAppraiserAssignment,
    externalAppraisingEntityName,
    open,
    setOpen,
    modalPrimaryButtonLabel = 'Übernehmen',
    copy,
    userLinkCopy,
}: AppraiserAssignmentProps) {
    const resolvedCopy = { ...DEFAULT_APPRAISER_ASSIGNMENT_COPY, ...copy }
    const modalCopy = { ...DEFAULT_APPRAISER_ASSIGNMENT_COPY.modal, ...copy?.modal }
    const presentations = resolveAppraiserAssignment({
        assignment: appraiserAssignment,
        users: users ?? [],
        organizationName: externalAppraisingEntityName,
    })
    const assignmentList = (
        <UserLinkList
            presentations={presentations}
            size={size}
            maxWidth={userLinkMaxWidth}
            copy={userLinkCopy}
            showAll={displayMode === 'expanded'}
            showNonUserChips={displayMode === 'expanded'}
        />
    )

    if (editAppraiserAssignment && !disabled) {
        let assignmentControl = assignmentList
        if (presentations.length === 0) {
            assignmentControl = <TinyButton label={resolvedCopy.addButton} leftIcon={<PlusIcon />} onClick={() => setOpen(true)} />
        } else if (displayMode === 'compact') {
            assignmentControl = (
                <StyledUserAssignmentButton onClick={() => setOpen(true)}>
                    {assignmentList}
                    <PencilSimpleIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconDefault} />
                </StyledUserAssignmentButton>
            )
        }

        return (
            <>
                {assignmentControl}
                <AppraiserAssignmentModal
                    open={open}
                    onClose={() => setOpen(false)}
                    onSubmitCallback={editAppraiserAssignment}
                    appraiserAssignment={appraiserAssignment}
                    users={users}
                    isEditingAppraiserAssignment={isPersistingAppraiserAssignment}
                    primaryButtonLabel={modalPrimaryButtonLabel}
                    copy={modalCopy}
                    userLinkCopy={userLinkCopy}
                />
            </>
        )
    }

    return (
        <Box padding={size === 'medium' ? tokens.SizingSpacing100 : tokens.SizingSpacingNone} display="flex" alignItems="center">
            {assignmentList}
        </Box>
    )
}

export const StyledUserAssignmentButton = styled(ButtonBase)(() => ({
    width: 'fit-content',
    borderRadius: tokens.SizingCornerS,
    padding: tokens.SizingSpacing100,
    gap: tokens.SizingSpacing150,
    '&:hover': {
        backgroundColor: tokens.ColorSurfaceHover,
    },
    '&:active': {
        backgroundColor: tokens.ColorSurfacePressed,
    },
    '& *': {
        cursor: 'pointer',
    },
}))
